import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/Button';
import { Search, X } from 'lucide-react';
import { UserProfileStatus } from '@/types/userProfile';

export interface ProfileFilters {
  search?: string;
  status?: UserProfileStatus | 'all';
  onboardingCompleted?: 'all' | 'true' | 'false';
  completionMin?: number;
  completionMax?: number;
  country?: string;
  state?: string;
  city?: string;
  dateFrom?: string;
  dateTo?: string;
}

interface ProfileFiltersProps {
  filters: ProfileFilters;
  onFiltersChange: (filters: ProfileFilters) => void;
  onReset: () => void;
}

export const ProfileFilters: React.FC<ProfileFiltersProps> = ({
  filters,
  onFiltersChange,
  onReset,
}) => {
  const updateFilter = (key: keyof ProfileFilters, value: string | number | undefined) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const hasActiveFilters = Object.entries(filters).some(
    ([key, value]) => key !== 'search' && value !== undefined && value !== 'all' && value !== ''
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Filters</CardTitle>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={onReset}>
              <X className="h-4 w-4 mr-1" />
              Reset
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {/* Search */}
          <div className="relative md:col-span-2 lg:col-span-4">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by user ID, email, username, or name..."
              value={filters.search || ''}
              onChange={(e) => updateFilter('search', e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Status */}
          <Select
            value={filters.status || 'all'}
            onValueChange={(value) => updateFilter('status', value === 'all' ? undefined : value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value={UserProfileStatus.DRAFT}>Draft</SelectItem>
              <SelectItem value={UserProfileStatus.PENDING}>Pending</SelectItem>
              <SelectItem value={UserProfileStatus.ACTIVE}>Active</SelectItem>
              <SelectItem value={UserProfileStatus.INACTIVE}>Inactive</SelectItem>
              <SelectItem value={UserProfileStatus.SUSPENDED}>Suspended</SelectItem>
            </SelectContent>
          </Select>

          {/* Onboarding */}
          <Select
            value={filters.onboardingCompleted || 'all'}
            onValueChange={(value) => updateFilter('onboardingCompleted', value === 'all' ? undefined : value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Onboarding Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="true">Completed</SelectItem>
              <SelectItem value="false">In Progress</SelectItem>
            </SelectContent>
          </Select>

          {/* Completion Min */}
          <Input
            type="number"
            placeholder="Min Completion %"
            min="0"
            max="100"
            value={filters.completionMin || ''}
            onChange={(e) => updateFilter('completionMin', e.target.value ? Number(e.target.value) : undefined)}
          />

          {/* Completion Max */}
          <Input
            type="number"
            placeholder="Max Completion %"
            min="0"
            max="100"
            value={filters.completionMax || ''}
            onChange={(e) => updateFilter('completionMax', e.target.value ? Number(e.target.value) : undefined)}
          />

          {/* Country */}
          <Input
            placeholder="Country"
            value={filters.country || ''}
            onChange={(e) => updateFilter('country', e.target.value || undefined)}
          />

          {/* State */}
          <Input
            placeholder="State"
            value={filters.state || ''}
            onChange={(e) => updateFilter('state', e.target.value || undefined)}
          />

          {/* City */}
          <Input
            placeholder="City"
            value={filters.city || ''}
            onChange={(e) => updateFilter('city', e.target.value || undefined)}
          />

          {/* Date From */}
          <Input
            type="date"
            placeholder="Date From"
            value={filters.dateFrom || ''}
            onChange={(e) => updateFilter('dateFrom', e.target.value || undefined)}
          />

          {/* Date To */}
          <Input
            type="date"
            placeholder="Date To"
            value={filters.dateTo || ''}
            onChange={(e) => updateFilter('dateTo', e.target.value || undefined)}
          />
        </div>
      </CardContent>
    </Card>
  );
};







